/**
 * The two chart shapes of the line tabs, both on BaseChart:
 *
 * - LineStationsChart: one column per machine, in line order (optionally
 *   stacked), for totals over the selected period and shifts.
 * - LineShiftsChart: one series per machine over the shifts, for KPIs that
 *   are a value per shift (reject ratio, MTBF), like the line efficiency chart.
 *
 * A click on a machine's column opens that machine.
 */

import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BaseChart, { type CategoryPointClick } from "../charts/BaseChart";
import { useThemedCharts } from "../../hooks/useThemedCharts";
import {
  compareCategories,
  stationAxisLabel,
  type LineStation,
  type NamedValue,
} from "./lineStations";

const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );

const formatNumber = (v: number, decimals: number) =>
  v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals });

/** Opens a machine of the line, as a click in the machines tab does. */
function useOpenMachine(lineId: string) {
  const navigate = useNavigate();
  return useCallback(
    (station: LineStation | undefined) => {
      if (!station) return;
      navigate(`/machine/${station.sn}`, {
        state: { machineName: station.name, lineId },
      });
    },
    [navigate, lineId],
  );
}

export interface StationStack {
  name: string;
  color?: string;
  /** Value per machine (sn), in the chart's unit. */
  values: Map<string, number>;
}

interface LineStationsChartProps {
  lineId: string;
  stations: LineStation[];
  stacks: StationStack[];
  yTitle: string;
  /** Appended to values in the tooltip (" h", "%"). */
  unit?: string;
  decimals?: number;
  /** Largest parts per machine (sn) for the tooltip, in the chart's unit. */
  details?: Map<string, NamedValue[]>;
  loading: boolean;
  noDataMessage: string;
  filename: string;
}

export const LineStationsChart: React.FC<LineStationsChartProps> = ({
  lineId,
  stations,
  stacks,
  yTitle,
  unit = "",
  decimals = 1,
  details,
  loading,
  noDataMessage,
  filename,
}) => {
  const { t } = useTranslation();
  const openMachine = useOpenMachine(lineId);

  const hasData = stacks.some((st) => [...st.values.values()].some((v) => v > 0));
  const categories = useMemo(() => stations.map(stationAxisLabel), [stations]);
  const series = useMemo(
    () =>
      hasData
        ? stacks.map((st) => ({
            name: st.name,
            color: st.color,
            data: stations.map((s) => {
              const v = st.values.get(s.sn);
              return v === undefined ? null : Math.round(v * 10 ** decimals) / 10 ** decimals;
            }),
          }))
        : [],
    [hasData, stacks, stations, decimals],
  );

  const topLabel = t("lineKpi.topCauses");
  const outputLabel = t("lineKpi.outputMachine");
  const tooltip = useMemo(
    () => ({
      formatter: function (this: unknown) {
        const ctx = this as {
          x?: number;
          y?: number;
          total?: number;
          point?: { index?: number };
          series: { name: string };
        };
        const station = stations[ctx.point?.index ?? ctx.x ?? 0];
        if (!station) return "";
        let html = `<b>${escapeHtml(stationAxisLabel(station))}</b>`;
        if (station.isOutput) html += `<br/><span style="opacity:.7">★ ${escapeHtml(outputLabel)}</span>`;
        html += `<br/>${escapeHtml(ctx.series.name)}: <b>${formatNumber(ctx.y ?? 0, decimals)}${escapeHtml(unit)}</b>`;
        if (stacks.length > 1 && ctx.total !== undefined) {
          html += `<br/>${escapeHtml(t("lineKpi.total"))}: ${formatNumber(ctx.total, decimals)}${escapeHtml(unit)}`;
        }
        const parts = details?.get(station.sn)?.slice(0, 3) ?? [];
        if (parts.length) {
          html += `<br/><span style="opacity:.7">${escapeHtml(topLabel)}</span>`;
          for (const p of parts) {
            html += `<br/>${escapeHtml(p.name)}: ${formatNumber(p.value, decimals)}${escapeHtml(unit)}`;
          }
        }
        return html;
      } as unknown as () => string,
    }),
    [stations, stacks.length, details, decimals, unit, t, topLabel, outputLabel],
  );

  const customOptions = useMemo(
    () => (stacks.length > 1 ? { plotOptions: { column: { stacking: "normal" as const } } } : {}),
    [stacks.length],
  );

  const onPointClick = useCallback(
    (p: CategoryPointClick) => openMachine(stations[p.index]),
    [openMachine, stations],
  );

  return (
    <BaseChart
      type="column"
      height={380}
      categories={categories}
      series={series}
      yAxis={{ min: 0, title: yTitle }}
      loading={loading}
      legend={{ enabled: stacks.length > 1, position: "bottom" }}
      tooltip={tooltip}
      customOptions={customOptions}
      panZoom={{ enabled: false, mouseWheel: false, pinchZoom: false }}
      onPointClick={onPointClick}
      noDataMessage={noDataMessage}
      exporting={{ enabled: true, filename }}
    />
  );
};

interface LineShiftsChartProps {
  lineId: string;
  stations: LineStation[];
  /** Per machine (sn): value per "YYYY-MM-DD SHIFT", in the chart's unit. */
  values: Map<string, Map<string, number>>;
  yTitle: string;
  unit?: string;
  decimals?: number;
  yMax?: number;
  loading: boolean;
  noDataMessage: string;
  filename: string;
  /** Chart type of its comments ("line_…", stored under the line). */
  commentChartType: string;
}

export const LineShiftsChart: React.FC<LineShiftsChartProps> = ({
  lineId,
  stations,
  values,
  yTitle,
  unit = "",
  decimals = 1,
  yMax,
  loading,
  noDataMessage,
  filename,
  commentChartType,
}) => {
  const { getSeriesColor } = useThemedCharts();

  const { categories, series } = useMemo(() => {
    const cats = new Set<string>();
    for (const perShift of values.values()) for (const c of perShift.keys()) cats.add(c);
    const sorted = [...cats].sort(compareCategories);
    const built = stations
      .filter((s) => values.get(s.sn)?.size)
      .map((s) => ({
        name: stationAxisLabel(s),
        // the colour follows the machine's place in the line, not its index among machines with data
        color: getSeriesColor(stations.indexOf(s)),
        data: sorted.map((c) => {
          const v = values.get(s.sn)?.get(c);
          return v === undefined ? null : Math.round(v * 10 ** decimals) / 10 ** decimals;
        }),
      }));
    return { categories: sorted, series: built };
  }, [values, stations, getSeriesColor, decimals]);

  return (
    <BaseChart
      commentProps={{ chartType: commentChartType, machineBK: lineId }}
      type="column"
      height={400}
      categories={categories}
      series={series}
      yAxis={{ min: 0, max: yMax, title: yTitle }}
      loading={loading}
      legend={{ enabled: true, position: "right" }}
      tooltip={{ suffix: unit }}
      navigator={{ enabled: true, height: 70, margin: 25 }}
      noDataMessage={noDataMessage}
      exporting={{ enabled: true, filename }}
    />
  );
};
